import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import { RefreshCw, Edit3, Save, Upload, AlertCircle, Check, ExternalLink, Lock, Ban, RotateCcw } from 'lucide-react';
import { DataGrid, GridCol } from './ui/DataGrid';
import { Thumb } from './ui/Thumb';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { Textarea } from './ui/arc/textarea';
import { MetricCard } from './ui/arc/metric-card';
import { EmptyState } from './ui/arc/empty-state';
import SegmentedControl from './ui/arc/segmented-control';
import { Dialog, DialogContent } from './ui/arc/dialog';
import { PageHeader, ScreenSkeleton } from './costs/plata-kit';

type Status = 'staged' | 'reviewed' | 'published' | 'failed' | 'skipped';

interface StagingRow {
    id: number;
    ean: string;
    titulo: string;
    formato: string | null;
    idioma: string | null;
    categoria_taschen: string | null;
    precio_eur: number | null;
    costo_ars: number | null;
    precio_venta_ars: number;
    taschen_section: string | null;
    taschen_url: string | null;
    taschen_internal_code: string | null;
    descripcion: string | null;
    imagenes_urls: string[];
    autor: string | null;
    editor: string | null;
    paginas: number | null;
    dimensiones: string | null;
    peso_kg: number | null;
    idioma_publicacion: string | null;
    tn_category_ids: number[];
    weight_kg: number | null;
    status: Status;
    tn_product_id: number | null;
    tn_error: string | null;
    scraped_at: string | null;
    published_at: string | null;
    tn_size_code: string | null;
    tn_sku: string | null;
}

const SIZE_LABELS: Record<string, string> = {
    M: 'Mediano',
    L: 'Grande',
    XL: 'Extra Grande',
    XXL: 'XXL',
};

interface Props {
    userEmail: string | undefined;
}

const ALLOWED_EMAILS = ['martin@krilic.com.ar', 'stephanie@krilic.com.ar'];

const STATUS_LABEL: Record<Status, { text: string; color: string }> = {
    staged: { text: 'Pendiente', color: '#6b7280' },
    reviewed: { text: 'Revisado', color: '#3b82f6' },
    published: { text: 'Publicado', color: '#16a34a' },
    failed: { text: 'Falló', color: '#dc2626' },
    skipped: { text: 'Saltado', color: '#9ca3af' },
};

const fmtPrice = (n: number | null) =>
    n != null ? `$${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })}` : '—';

export default function TnImport({ userEmail }: Props) {
    const [rows, setRows] = useState<StagingRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<Status | 'all'>('all');
    const [editing, setEditing] = useState<StagingRow | null>(null);
    const [publishing, setPublishing] = useState<Set<number>>(new Set());
    const [batchPublishing, setBatchPublishing] = useState(false);

    const allowed = !!userEmail && ALLOWED_EMAILS.includes(userEmail);

    async function load() {
        setLoading(true);
        const { data, error } = await supabase
            .from('tn_import_staging').select('*').order('id', { ascending: true });
        if (error) console.error(error);
        setRows((data || []) as StagingRow[]);
        setLoading(false);
    }

    useEffect(() => {
        if (allowed) load();
    }, [allowed]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r => {
            if (filterStatus !== 'all' && r.status !== filterStatus) return false;
            if (q && !r.titulo.toLowerCase().includes(q) && !r.ean.includes(q)) return false;
            return true;
        });
    }, [rows, search, filterStatus]);

    const stats = useMemo(() => {
        const counts = { staged: 0, reviewed: 0, published: 0, failed: 0, skipped: 0, ready: 0 };
        for (const r of rows) {
            counts[r.status]++;
            // "Ready" = tiene descripción, al menos 1 imagen y aún no publicado
            if (r.status !== 'published' && r.descripcion && (r.imagenes_urls?.length || 0) > 0) {
                counts.ready++;
            }
        }
        return counts;
    }, [rows]);

    async function publishOne(row: StagingRow) {
        if (publishing.has(row.id)) return;
        if (!row.descripcion || (row.imagenes_urls?.length || 0) === 0) {
            alert('Faltan datos: el producto necesita descripción y al menos una imagen para publicarse.');
            return;
        }
        setPublishing(prev => new Set(prev).add(row.id));
        try {
            const { data, error } = await supabase.functions.invoke('tn-publish-product', {
                body: { staging_id: row.id },
            });
            if (error) throw error;
            if (!data?.ok) throw new Error(JSON.stringify(data?.error || data));
            await load();
        } catch (e: any) {
            alert(`Error publicando ${row.titulo}: ${e?.message || e}`);
        } finally {
            setPublishing(prev => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
            });
        }
    }

    async function publishBatch() {
        const ready = rows.filter(r =>
            r.status !== 'published' &&
            r.descripcion &&
            (r.imagenes_urls?.length || 0) > 0
        );
        if (ready.length === 0) {
            alert('No hay productos listos para publicar.');
            return;
        }
        if (!window.confirm(`Publicar ${ready.length} producto${ready.length === 1 ? '' : 's'} a Tiendanube? Esta acción no se puede deshacer.`)) return;
        setBatchPublishing(true);
        try {
            for (const r of ready) await publishOne(r);
        } finally {
            setBatchPublishing(false);
        }
    }

    async function markSkipped(row: StagingRow) {
        if (!window.confirm(`Saltar "${row.titulo}"? No se va a publicar.`)) return;
        await supabase.from('tn_import_staging').update({ status: 'skipped' }).eq('id', row.id);
        await load();
    }

    async function resetStatus(row: StagingRow) {
        await supabase.from('tn_import_staging').update({ status: 'staged', tn_error: null }).eq('id', row.id);
        await load();
    }

    if (!allowed) {
        return (
            <div className="tw w-full min-w-0 pb-12 pt-1">
                <div className="rounded-[var(--radius-surface)] border border-line bg-card">
                    <EmptyState icon={<Lock size={20} />} title="Acceso restringido" description="Solo administradores." />
                </div>
            </div>
        );
    }

    const isReady = (r: StagingRow) => !!(r.descripcion && (r.imagenes_urls?.length || 0) > 0);
    const cols: GridCol<StagingRow>[] = [
        { key: 'img', header: '', width: 'w-14', render: r => <Thumb src={r.imagenes_urls?.[0]} name={r.titulo} size="md" /> },
        {
            key: 'titulo', header: 'Producto', sort: r => `${r.titulo} ${r.ean}`, render: r => (
                <div className="min-w-[220px] max-w-[420px]">
                    <div className="truncate font-semibold text-ink" title={r.titulo}>{r.titulo}</div>
                    <div className="truncate text-[length:var(--text-xs)] text-soft">
                        EAN {r.ean}{r.formato ? ` · ${r.formato}` : ''}{r.idioma ? ` · ${r.idioma}` : ''}
                    </div>
                </div>
            ),
        },
        { key: 'size', header: 'Tamaño', sort: r => r.tn_size_code || '', hideOnMobile: true, render: r => r.tn_size_code ? <Badge size="sm" title={SIZE_LABELS[r.tn_size_code] || r.tn_size_code}>{r.tn_size_code}</Badge> : <span className="text-faint">—</span> },
        { key: 'costo', header: 'Costo', align: 'right', hideOnMobile: true, sort: r => r.costo_ars ?? 0, render: r => fmtPrice(r.costo_ars) },
        { key: 'venta', header: 'Venta', align: 'right', sort: r => Number(r.precio_venta_ars) || 0, render: r => <span className="font-semibold text-ink">{fmtPrice(r.precio_venta_ars)}</span> },
        {
            key: 'markup', header: 'Markup', align: 'right', hideOnMobile: true,
            sort: r => (r.costo_ars && r.precio_venta_ars) ? Number(r.precio_venta_ars) / Number(r.costo_ars) : null,
            render: r => (r.costo_ars && r.precio_venta_ars) ? <span className="tabular-nums">{(Number(r.precio_venta_ars) / Number(r.costo_ars)).toFixed(2).replace('.', ',')}×</span> : <span className="text-faint">—</span>,
        },
        {
            key: 'status', header: 'Estado', sort: r => r.status, render: r => (
                <div className="flex flex-wrap gap-1">
                    <Badge size="sm" tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status].text}</Badge>
                    {r.status !== 'published' && r.status !== 'skipped' && !r.descripcion && <Badge size="sm" tone="warning">sin descripción</Badge>}
                    {r.status !== 'published' && r.status !== 'skipped' && !(r.imagenes_urls?.length) && <Badge size="sm" tone="warning">sin imagen</Badge>}
                </div>
            ),
        },
        {
            key: 'acc', header: '', align: 'right', render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {r.taschen_url && (
                        <a href={r.taschen_url} target="_blank" rel="noreferrer" title="Ver en Taschen" aria-label="Ver en Taschen"
                            className="inline-flex h-[var(--control-height-sm)] items-center rounded-[var(--radius-control)] px-2 text-soft hover:bg-muted hover:text-ink">
                            <ExternalLink size={14} />
                        </a>
                    )}
                    <Button variant="ghost" size="sm" title="Editar" aria-label="Editar" onClick={() => setEditing(r)}><Edit3 size={14} /></Button>
                    {r.status === 'failed' && <Button variant="ghost" size="sm" title="Volver a pendiente" aria-label="Volver a pendiente" onClick={() => resetStatus(r)}><RotateCcw size={14} /></Button>}
                    {r.status !== 'published' && r.status !== 'skipped' && <>
                        <Button variant="ghost" size="sm" title="Saltar (no publicar)" aria-label="Saltar" onClick={() => markSkipped(r)}><Ban size={14} /></Button>
                        <Button size="sm" onClick={() => publishOne(r)} disabled={publishing.has(r.id) || !isReady(r)} loading={publishing.has(r.id)}
                            title={isReady(r) ? 'Publicar a Tiendanube' : 'Faltan descripción o imágenes'}>
                            <Upload size={14} /> Publicar
                        </Button>
                    </>}
                </div>
            ),
        },
    ];

    const statusOptions = (['all', 'staged', 'reviewed', 'published', 'failed', 'skipped'] as const).map(s => ({
        value: s,
        label: s === 'all' ? 'Todos' : STATUS_LABEL[s].text,
        accessory: <Badge size="sm" tone={s === 'failed' && stats.failed ? 'danger' : 'neutral'}>{s === 'all' ? rows.length : stats[s]}</Badge>,
    }));

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <PageHeader title="Importar a Tiendanube"
                description="Libros de TASCHEN cargados en staging: revisá título, precio, tamaño e imágenes, y publicalos en la tienda. Solo se publican los que tienen descripción y al menos una imagen."
                actions={<>
                    <Button variant="secondary" onClick={load}><RefreshCw size={16} /> Actualizar</Button>
                    <Button onClick={publishBatch} disabled={batchPublishing || stats.ready === 0} loading={batchPublishing}>
                        <Upload size={16} /> Publicar listos ({stats.ready})
                    </Button>
                </>} />

            {loading && !rows.length ? <ScreenSkeleton /> : <>
                <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <MetricCard label="En staging" value={rows.length} locale="es-AR" context={`${stats.staged} pendientes · ${stats.reviewed} revisados`} />
                    <MetricCard label="Listos para publicar" value={stats.ready} locale="es-AR" context="con descripción e imagen, sin publicar" />
                    <MetricCard label="Publicados" value={stats.published} locale="es-AR" context={`${stats.skipped} saltados`} />
                    <MetricCard label="Fallaron" value={stats.failed} locale="es-AR" context={stats.failed ? 'revisá el error en el detalle de cada uno' : 'ningún error al publicar'} />
                </section>

                <div className="mb-4 min-w-0">
                    <SegmentedControl label="Estado" value={filterStatus} onValueChange={v => setFilterStatus(v as Status | 'all')} options={statusOptions} />
                </div>

                <DataGrid rows={filtered} cols={cols} loading={loading} dense pageSize={50}
                    searchPlaceholder="Buscar por título o EAN…"
                    filtersActive={filterStatus !== 'all'} onClearFilters={() => setFilterStatus('all')}
                    emptyTitle="Sin productos en staging" emptyText="Cargá productos en tn_import_staging para publicarlos desde acá."
                    rowClassName={r => r.status === 'skipped' && 'opacity-60'}
                    renderExpanded={r => <RowDetail row={r} />} />
            </>}

            {editing && (
                <EditModal
                    row={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

const STATUS_TONE: Record<Status, 'neutral' | 'info' | 'success' | 'danger' | 'warning'> = {
    staged: 'neutral', reviewed: 'info', published: 'success', failed: 'danger', skipped: 'neutral',
};

/** Detalle desplegable de la fila: descripción, imágenes, error de TN y link al producto. */
function RowDetail({ row }: { row: StagingRow }) {
    return (
        <div className="flex flex-col gap-3 py-1 text-[length:var(--text-sm)]">
            {row.tn_error && (
                <div className="flex items-start gap-2 text-[var(--danger)]"><AlertCircle size={14} className="mt-0.5 shrink-0" /> <span className="break-words">{row.tn_error}</span></div>
            )}
            {row.tn_product_id && (
                <div className="flex items-center gap-1.5 text-[var(--success)]"><Check size={14} /> Publicado en Tiendanube: <b>#{row.tn_product_id}</b>{row.tn_sku && <span className="text-soft"> · SKU {row.tn_sku}</span>}</div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-soft">
                {row.taschen_section && <span>Sección: <b className="text-ink">{row.taschen_section}</b></span>}
                {row.autor && <span>Autor: <b className="text-ink">{row.autor}</b></span>}
                {row.paginas && <span>{row.paginas} páginas</span>}
                {row.dimensiones && <span>{row.dimensiones}</span>}
                {(row.peso_kg ?? row.weight_kg) != null && <span>{row.peso_kg ?? row.weight_kg} kg</span>}
                {row.precio_eur != null && <span>€ {Number(row.precio_eur).toLocaleString('es-AR')}</span>}
            </div>
            {row.descripcion
                ? <p className="max-w-[100ch] whitespace-pre-line text-soft">{row.descripcion}</p>
                : <p className="flex items-center gap-1.5 text-[var(--warning)]"><AlertCircle size={14} /> Sin descripción: no se puede publicar.</p>}
            {(row.imagenes_urls?.length || 0) > 0 && (
                <div className="flex flex-wrap gap-2">
                    {row.imagenes_urls.slice(0, 8).map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer"><Thumb src={u} name={row.titulo} size="lg" /></a>
                    ))}
                    {row.imagenes_urls.length > 8 && <span className="self-center text-soft">+{row.imagenes_urls.length - 8}</span>}
                </div>
            )}
        </div>
    );
}

interface EditModalProps {
    row: StagingRow;
    onClose: () => void;
    onSaved: () => void;
}

function EditModal({ row, onClose, onSaved }: EditModalProps) {
    const [titulo, setTitulo] = useState(row.titulo);
    const [descripcion, setDescripcion] = useState(row.descripcion || '');
    const [precioVenta, setPrecioVenta] = useState<string>(String(row.precio_venta_ars || 0));
    const [pesoKg, setPesoKg] = useState<string>(String(row.peso_kg ?? row.weight_kg ?? ''));
    const [imagesText, setImagesText] = useState<string>((row.imagenes_urls || []).join('\n'));
    const [taschenUrl, setTaschenUrl] = useState(row.taschen_url || '');
    const [sizeCode, setSizeCode] = useState<string>(row.tn_size_code || 'M');
    const [sku, setSku] = useState<string>(row.tn_sku || `${row.tn_size_code || 'M'}-${row.ean}`);
    const [saving, setSaving] = useState(false);

    // Cuando cambia el tamaño y el SKU sigue con formato standard, auto-actualizo el SKU
    // Mantiene la medida si está presente: {SIZE}-{W}x{H}-{EAN} o {SIZE}-{EAN}
    useEffect(() => {
        const measureMatch = sku.match(/^(?:M|L|XL|XXL)-(\d+(?:\.\d+)?x\d+(?:\.\d+)?)-/);
        if (measureMatch) {
            setSku(`${sizeCode}-${measureMatch[1]}-${row.ean}`);
            return;
        }
        const stdPattern = /^(M|L|XL|XXL)-/;
        if (stdPattern.test(sku)) {
            setSku(`${sizeCode}-${row.ean}`);
        }
    // eslint-disable-next-line
    }, [sizeCode]);

    async function handleSave() {
        setSaving(true);
        try {
            const imagenes = imagesText.split('\n').map(s => s.trim()).filter(s => s.startsWith('http'));
            const { error } = await supabase.from('tn_import_staging').update({
                titulo: titulo.trim(),
                descripcion: descripcion.trim() || null,
                precio_venta_ars: Number(precioVenta) || 0,
                weight_kg: pesoKg.trim() === '' ? null : Number(pesoKg),
                peso_kg: pesoKg.trim() === '' ? null : Number(pesoKg),
                imagenes_urls: imagenes,
                taschen_url: taschenUrl.trim() || null,
                tn_size_code: sizeCode || null,
                tn_sku: sku.trim() || null,
                status: 'reviewed',
            }).eq('id', row.id);
            if (error) throw error;
            onSaved();
        } catch (e: any) {
            alert('Error: ' + (e?.message || e));
        } finally {
            setSaving(false);
        }
    }

    const selectCls = 'h-[var(--control-height-md)] w-full rounded-[var(--radius-control)] border border-line bg-card px-2 text-[length:var(--text-sm)] font-normal text-ink';
    return (
        <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent title="Editar producto" description={`EAN ${row.ean} · al guardar queda como Revisado`} className="!w-[min(720px,calc(100vw-32px))]">
                <div className="flex flex-col gap-3">
                    <Input label="Título" value={titulo} onChange={e => setTitulo(e.target.value)} />
                    <Textarea label="Descripción" rows={6} value={descripcion} onChange={e => setDescripcion(e.target.value)} />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input label="Precio venta ARS" type="number" step="0.01" value={precioVenta} onChange={e => setPrecioVenta(e.target.value)} />
                        <Input label="Peso (kg)" type="number" step="0.001" value={pesoKg} onChange={e => setPesoKg(e.target.value)} />
                        <label className="flex flex-col gap-1.5 text-[length:var(--text-sm)] font-medium text-ink">
                            Tamaño
                            <select value={sizeCode} onChange={e => setSizeCode(e.target.value)} className={selectCls}>
                                <option value="M">M · Mediano (Basic Art / Bibliotheca)</option>
                                <option value="L">L · Grande (Anniversary / Icon)</option>
                                <option value="XL">XL · Extra Grande (estándar)</option>
                                <option value="XXL">XXL · Jumbo (Extra Large / Jumbo)</option>
                            </select>
                        </label>
                        <Input label="SKU (con código de tamaño)" value={sku} onChange={e => setSku(e.target.value)} placeholder="M-9783836588164" className="font-mono" />
                    </div>
                    <Input label="URL Taschen (referencia)" value={taschenUrl} onChange={e => setTaschenUrl(e.target.value)} placeholder="https://www.taschen.com/es/books/…" />
                    <Textarea label="Imágenes (una URL por línea — la primera es la portada)" rows={5} value={imagesText} onChange={e => setImagesText(e.target.value)} placeholder={'https://…/img1.jpg\nhttps://…/img2.jpg'} />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button onClick={handleSave} loading={saving}><Save size={16} /> {saving ? 'Guardando…' : 'Guardar'}</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
